import { useState, useContext, useRef, useEffect } from "react";
import axios from 'axios';

import { AppContext } from '../../context.js';
import Editor from './editor.js';

function Assistant() {
  const context = useContext(AppContext);

  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Load existing conversation on mount
  useEffect(() => {
    const loadCurrentConversation = async () => {
      try {
        const response = await axios.get('/api/admin/assistants');
        if (response.data && response.data.id) {
          setConversationId(response.data.id);
          // Filter to only show user and assistant messages (not tool messages)
          const chatMessages = (response.data.messages || [])
            .filter(msg => msg.role === 'user' || msg.role === 'assistant')
            .map(msg => ({
              id: msg.id,
              content: msg.content,
              role: msg.role,
              createdAt: msg.createdAt,
            }));
          setMessages(chatMessages);
        }
      } catch (e) {
        // No existing conversation or error - start fresh
        console.log('No existing conversation found');
      } finally {
        setInitialLoading(false);
      }
    };

    loadCurrentConversation();
  }, []);

  useEffect(() => {
    // Should be timeout to ensure messages are rendered
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  }, [messages]);

  const handleSend = async () => {
    if (!inputText.trim() || loading) return;

    const userMessage = {
      id: Date.now(),
      content: inputText,
      role: "user",
      createdAt: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText("");
    setLoading(true);

    try {
      const response = await axios.post('/api/admin/assistants/message', {
        conversationId: conversationId,
        message: inputText,
      });

      // Save conversation ID for future messages
      if (response.data.conversationId) {
        setConversationId(response.data.conversationId);
      }

      // Add assistant response
      if (response.data.message) {
        const assistantMessage = {
          id: response.data.message.id || Date.now() + 1,
          content: response.data.message.content,
          role: "assistant",
          createdAt: response.data.message.createdAt || new Date().toISOString(),
        };
        setMessages((prev) => [...prev, assistantMessage]);
      }
    } catch (e) {
      context.addError(e);
      // Add error message to chat
      setMessages((prev) => [...prev, {
        id: Date.now() + 1,
        content: "Sorry, I encountered an error. Please try again.",
        role: "assistant",
        createdAt: new Date().toISOString(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setConversationId(null);
  };

  return (
    <div style={{display: "flex", flexDirection: "column", height: "100%", overflow: "hidden"}}>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="typo-strong-300">Assistant</div>
        <div className="item grey typo-normal-150" onClick={handleNewChat}>New chat</div>
      </div>

      {/* Messages Container */}
      <div style={{
        flex: 1,
        overflowY: "auto",
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}>
        {initialLoading ? (
          <div style={{display: "flex", alignItems: "center", justifyContent: "center", height: "100%"}}>Loading...</div>
        ) : messages.length === 0 ? (
          <div style={{display: "flex", alignItems: "center", justifyContent: "center", height: "100%"}}>Start a conversation with the AI assistant</div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={"item typo-normal-150 " + (message.role === "user" ? "blue" : "grey")}
              style={{
                alignSelf: message.role === "user" ? "flex-end" : "flex-start",
                maxWidth: "80%",
              }}
            >
              <Editor
                value={message.content}
                readOnly={true}
                toolbar=""
                className="text-editor borderless"
              />
            </div>
          ))
        )}

        {loading && (
          <div className="item grey typo-normal-150" style={{alignSelf: "flex-start", maxWidth: "80%"}}>
            Thinking...
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Container - Sticky Bottom */}
      <div className="pt-2" style={{backgroundColor: "#fff", display: "flex", gap: "12px", alignItems: "center"}}>
        <Editor
          placeholder="Type a message..."
          value={inputText}
          onChange={setInputText}
          onChangeID={null}
          onEnter={handleSend}
          className="text-editor borderless w-100"
          style={{flex: 1}}
        />

        <div className="item grey typo-normal-150" onClick={handleSend}>Send</div>
      </div>
    </div>
  );
}

export default Assistant;
